import { Fragment, useMemo, useState } from 'react'
import type { AuthSession, StaffScheduleData } from '../../../types/plans'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { StaffScheduleVisitPopover } from './StaffScheduleVisitPopover'

type Appointment = StaffScheduleData['appointments'][number]

const ROW_PX = 28
const SLOT_MINUTES = 15
const FALLBACK_RANGE = { start: 9 * 60, end: 21 * 60 }

function toMinutes(time: string | undefined | null): number | null {
  if (!time) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(time.trim())
  if (!match) return null
  const hh = Number(match[1])
  const mm = Number(match[2])
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  return hh * 60 + mm
}

function formatSlot(minutes: number): string {
  const hh = Math.floor(minutes / 60).toString().padStart(2, '0')
  const mm = (minutes % 60).toString().padStart(2, '0')
  return `${hh}:${mm}`
}

function visitClasses(kind?: 'primary' | 'repeat'): string {
  if (kind === 'primary') {
    return 'border-emerald-300 bg-emerald-100 text-emerald-900 hover:bg-emerald-200'
  }
  if (kind === 'repeat') {
    return 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200'
  }
  return 'border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-200'
}

function appointmentStatusColor(code?: number | null, label?: string | null): string {
  const normalized = `${code ?? ''} ${label ?? ''}`.toLowerCase()
  if (normalized.includes('10001933') || normalized.includes('импл')) return 'bg-sky-500'
  if (normalized.includes('10001928') || normalized.includes('леч')) return 'bg-emerald-500'
  if (normalized.includes('4429') || normalized.includes('повтор')) return 'bg-amber-400'
  if (normalized.includes('первич')) return 'bg-fuchsia-500'
  if (normalized.includes('0')) return 'bg-slate-400'
  return 'bg-indigo-400'
}

function appointmentStatusTitle(appointment: Appointment): string {
  return [
    appointment.appointment_status_label,
    appointment.appointment_state,
    appointment.appointment_status_code != null ? `код ${appointment.appointment_status_code}` : null,
  ].filter(Boolean).join(' · ') || 'Статус назначения'
}

function doctorKey(name: string | undefined | null): string {
  return name?.trim() || 'Не указан'
}

interface PositionedAppointment {
  appointment: Appointment
  doctorIndex: number
  top: number
  height: number
  laneIndex: number
  laneCount: number
}

function buildPositioned(
  appointments: Appointment[],
  doctors: string[],
  rangeStart: number,
): PositionedAppointment[] {
  const grouped = new Map<number, Appointment[]>()
  doctors.forEach((_, idx) => grouped.set(idx, []))

  appointments.forEach((appt) => {
    const idx = doctors.indexOf(doctorKey(appt.doctor_name))
    if (idx < 0) return
    grouped.get(idx)?.push(appt)
  })

  const positioned: PositionedAppointment[] = []

  grouped.forEach((items, doctorIndex) => {
    const sorted = items
      .map((appt) => {
        const start = toMinutes(appt.start_time)
        const endRaw = toMinutes(appt.end_time)
        const end = endRaw !== null && start !== null && endRaw <= start ? start + SLOT_MINUTES : endRaw
        return { appt, start, end }
      })
      .filter((row): row is { appt: Appointment; start: number; end: number } => row.start !== null && row.end !== null)
      .sort((a, b) => a.start - b.start || a.end - b.end)

    const lanes: Array<{ end: number }> = []

    sorted.forEach(({ appt, start, end }) => {
      let laneIndex = lanes.findIndex((lane) => lane.end <= start)
      if (laneIndex === -1) {
        lanes.push({ end })
        laneIndex = lanes.length - 1
      } else {
        lanes[laneIndex] = { end }
      }
      positioned.push({
        appointment: appt,
        doctorIndex,
        top: ((start - rangeStart) / SLOT_MINUTES) * ROW_PX,
        height: Math.max(((end - start) / SLOT_MINUTES) * ROW_PX, ROW_PX - 2),
        laneIndex,
        laneCount: 0,
      })
    })

    const laneCount = Math.max(lanes.length, 1)
    positioned
      .filter((p) => p.doctorIndex === doctorIndex)
      .forEach((p) => {
        p.laneCount = laneCount
      })
  })

  return positioned
}

export function StaffScheduleTimeGrid({
  bootstrap,
  session,
  data,
  selectedDate,
  onSelectDate,
  canIssueSecretLinks = false,
  autoOpenSecretLinks = false,
  onIssuePatientSecretLink,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  data: StaffScheduleData
  selectedDate: string
  onSelectDate: (date: string) => void
  canIssueSecretLinks?: boolean
  autoOpenSecretLinks?: boolean
  onIssuePatientSecretLink?: (patientId: number) => Promise<string>
}) {
  const [openAppt, setOpenAppt] = useState<Appointment | null>(null)

  const appointmentsOfDay = useMemo(
    () => data.appointments.filter((a) => a.date === selectedDate),
    [data.appointments, selectedDate],
  )

  const doctors = useMemo(() => {
    const set = new Set<string>()
    appointmentsOfDay.forEach((a) => set.add(doctorKey(a.doctor_name)))
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'))
  }, [appointmentsOfDay])

  const branches = useMemo(() => {
    const set = new Set<string>()
    appointmentsOfDay.forEach((a) => {
      const v = a.branch_name?.trim()
      if (v) set.add(v)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'))
  }, [appointmentsOfDay])

  const [branchFilter, setBranchFilter] = useState<string>('all')

  const filteredAppointments = useMemo(() => {
    if (branchFilter === 'all') return appointmentsOfDay
    return appointmentsOfDay.filter((a) => (a.branch_name?.trim() || '') === branchFilter)
  }, [appointmentsOfDay, branchFilter])

  const filteredDoctors = useMemo(() => {
    const set = new Set<string>()
    filteredAppointments.forEach((a) => set.add(doctorKey(a.doctor_name)))
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'))
  }, [filteredAppointments])

  const range = useMemo(() => {
    if (filteredAppointments.length === 0) {
      return FALLBACK_RANGE
    }
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    filteredAppointments.forEach((a) => {
      const start = toMinutes(a.start_time)
      const end = toMinutes(a.end_time)
      if (start !== null && start < min) min = start
      if (end !== null && end > max) max = end
    })
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return FALLBACK_RANGE
    }
    const startRounded = Math.floor(min / 60) * 60
    const endRounded = Math.ceil(max / 60) * 60
    return { start: startRounded, end: Math.max(endRounded, startRounded + 60) }
  }, [filteredAppointments])

  const slots = useMemo(() => {
    const result: number[] = []
    for (let m = range.start; m < range.end; m += SLOT_MINUTES) {
      result.push(m)
    }
    return result
  }, [range])

  const positioned = useMemo(
    () => buildPositioned(filteredAppointments, filteredDoctors, range.start),
    [filteredAppointments, filteredDoctors, range.start],
  )

  const gridTemplateColumns = `60px repeat(${Math.max(filteredDoctors.length, 1)}, minmax(160px, 1fr))`
  const totalHeight = slots.length * ROW_PX

  return (
    <section className="overflow-hidden rounded-md border border-slate-300 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {data.days.map((day) => {
            const active = day.date === selectedDate
            return (
              <button
                key={day.date}
                type="button"
                onClick={() => onSelectDate(day.date)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-sky-400 hover:text-sky-700'
                }`}
              >
                {day.label}
                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {day.appointments}
                </span>
              </button>
            )
          })}
        </div>
        {branches.length > 1 ? (
          <div className="ml-auto flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Филиал:</span>
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-sky-400 focus:outline-none"
              value={branchFilter}
              onChange={(event) => setBranchFilter(event.target.value)}
            >
              <option value="all">Все ({appointmentsOfDay.length})</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {filteredDoctors.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-slate-500">
          На выбранный день записей нет.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-fit">
            <div
              className="sticky top-0 z-20 grid bg-slate-100 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-600"
              style={{ gridTemplateColumns }}
            >
              <div className="border-b border-r border-slate-300 px-2 py-1.5">Время</div>
              {filteredDoctors.map((d) => (
                <div
                  key={d}
                  className="truncate border-b border-r border-slate-300 px-2 py-1.5"
                  title={d}
                >
                  {d}
                </div>
              ))}
            </div>

            <div
              className="relative grid"
              style={{
                gridTemplateColumns,
                gridAutoRows: `${ROW_PX}px`,
                height: `${totalHeight}px`,
              }}
            >
              {slots.map((minute) => {
                const isHourMark = minute % 60 === 0
                const isHalf = minute % 60 === 30
                return (
                  <Fragment key={minute}>
                    <div
                      className={`flex items-start justify-end border-r border-slate-200 px-2 pt-0.5 text-[10px] leading-none ${
                        isHourMark ? 'border-t border-slate-300 font-semibold text-slate-700' : isHalf ? 'border-t border-slate-200 text-slate-500' : 'text-transparent'
                      }`}
                    >
                      {isHourMark ? formatSlot(minute) : isHalf ? formatSlot(minute) : '·'}
                    </div>
                    {filteredDoctors.map((d) => (
                      <div
                        key={`${d}:${minute}`}
                        className={`border-r border-slate-200 ${
                          isHourMark ? 'border-t border-slate-300' : isHalf ? 'border-t border-slate-200' : ''
                        }`}
                      />
                    ))}
                  </Fragment>
                )
              })}

              {positioned.map(({ appointment, doctorIndex, top, height, laneIndex, laneCount }) => {
                const widthPct = 100 / Math.max(laneCount, 1)
                const leftPct = widthPct * laneIndex
                return (
                  <button
                    key={appointment.id}
                    type="button"
                    onClick={() => setOpenAppt(appointment)}
                    className={`absolute overflow-hidden rounded-sm border px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition-colors ${visitClasses(appointment.visit_kind)}`}
                    style={{
                      gridColumnStart: doctorIndex + 2,
                      gridColumnEnd: doctorIndex + 3,
                      top: `${top}px`,
                      height: `${height}px`,
                      left: `calc(${leftPct}% + 1px)`,
                      width: `calc(${widthPct}% - 2px)`,
                    }}
                    title={`${appointment.start_time}–${appointment.end_time} · ${appointment.patient_name} · ${appointment.service_name}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${appointmentStatusColor(appointment.appointment_status_code, appointment.appointment_status_label)}`}
                          title={appointmentStatusTitle(appointment)}
                        />
                        <span className="truncate font-semibold">{appointment.patient_name}</span>
                      </span>
                      <span className="shrink-0 text-[10px] opacity-80">
                        {appointment.start_time}
                      </span>
                    </div>
                    {height >= ROW_PX * 1.6 ? (
                      <div className="truncate opacity-90">{appointment.service_name}</div>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-100" />
          Первичный
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-amber-300 bg-amber-100" />
          Повторный
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-sky-300 bg-sky-100" />
          Прочее
        </span>
        <span className="ml-auto">
          {filteredAppointments.length} визит(ов) · {filteredDoctors.length} врач(ей)
        </span>
      </div>

      {openAppt ? (
        <StaffScheduleVisitPopover
          appointment={openAppt}
          bootstrap={bootstrap}
          session={session}
          onClose={() => setOpenAppt(null)}
          canIssueSecretLinks={canIssueSecretLinks}
          autoOpenSecretLinks={autoOpenSecretLinks}
          onIssuePatientSecretLink={onIssuePatientSecretLink}
        />
      ) : null}
    </section>
  )
}
